<template>
  <div class="merchant-overview">
    <div style="display: flex; justify-content: space-between; align-items: center;">
      <h2 class="section-title">商户概览</h2>
    </div>
    <div v-if="!statsData" class="dashboard-skeleton-list">
      <!-- 3行 3列每列一个 -->
      <div v-for="n in 3" :key="n" class="dashboard-skeleton-item">
        <a-row :gutter="12" >
          <a-col :span="8" class="dashboard-skeleton-item-col">
            <div class="dashboard-skeleton-item-col-content">
              <a-skeleton active :title="false" />
            </div>
          </a-col>
          <a-col :span="8" class="dashboard-skeleton-item-col">
            <div class="dashboard-skeleton-item-col-content">
              <a-skeleton active :title="false" />
            </div>
          </a-col>
          <a-col :span="8" class="dashboard-skeleton-item-col">
            <div class="dashboard-skeleton-item-col-content">
              <a-skeleton active :title="false"/>
            </div>
          </a-col>
        </a-row>
      </div>
    </div>
    <!-- 使用 JeepayCard 组件展示统计卡片 -->
    <jeepay-card
      v-else
      :key="cardKey"
      :span="{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 2, xs: 1 }"
      :height="120"
      :add-authority="false"
      :req-card-list-func="getStatsList"
    >
      <!-- 卡片内容插槽 -->
      <template #cardContentSlot="{ record }">
        <div class="dashboard-stat-card">
          <!-- 右上角标签 -->
          <div class="card-tag" :class="record.tagClass">
            {{ record.tag }}
          </div>
          <!-- 主要数据 -->
          <div class="main-data">
            <div class="main-value">{{ formatValue(record.value, record.type) }}</div>
            <div class="main-title">{{ record.title }}</div>
          </div>
          <!-- 副数据 -->
          <div class="sub-data">
            <span class="sub-label">{{ record.subTitle }}</span>
            <span class="sub-value">{{ record.subValue }}</span>
          </div>
        </div>
      </template>
    </jeepay-card>
  </div>
</template>

<script>
import JeepayCard from '@/components/JeepayCard/JeepayCard'
import { formatAmount } from '@/utils/util'

export default {
  name: 'MerchantOverview',
  components: {
    JeepayCard
  },
  props: {
    statsData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      cardKey: 0 // 用于强制刷新卡片的 key
    }
  },
  watch: {
    statsData: {
      handler (newVal) {
        // console.log('statsData changed:', newVal)
        if (newVal && Object.keys(newVal).length > 0) {
          this.refreshCard()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // 获取统计卡片列表数据
    getStatsList () {
      return new Promise((resolve) => {
        // console.log('getStatsList called, statsData:', this.statsData)
        // console.log('statsData keys:', Object.keys(this.statsData))
        const statsList = [
          {
            title: '今日代收总额',
            value: Number(this.statsData.todayPayInOrderSum?.totalOrderAmount || 0),
            subTitle: '成功率',
            // 成功率（完成订单数 /总订单数）
            subValue: (this.statsData.todayPayInOrderSum?.successRate || 0) + '%' + '(' + Number(this.statsData.todayPayInOrderSum?.completeOrderCount || 0) + '/' + Number(this.statsData.todayPayInOrderSum?.totalOrderCount || 0) + ')',
            subType: 'percent',
            tag: '日',
            tagClass: 'tag-orange',
            type: 'amount'
          },
          {
            title: '昨日代收总额',
            value: Number(this.statsData.yesterdayPayInOrderSum?.totalOrderAmount || 0),
            subTitle: '成功率',
            subValue: (this.statsData.yesterdayPayInOrderSum?.successRate || 0) + '%' + '(' + Number(this.statsData.yesterdayPayInOrderSum?.completeOrderCount || 0) + '/' + Number(this.statsData.yesterdayPayInOrderSum?.totalOrderCount || 0) + ')',
            subType: 'percent',
            tag: '日',
            tagClass: 'tag-orange',
            type: 'amount'
          },
          {
            title: '代收总额',
            value: Number(this.statsData.allPayInOrderSum?.totalOrderAmount || 0),
            subTitle: '成功率',
            subValue: (this.statsData.allPayInOrderSum?.successRate || 0) + '%' + '(' + Number(this.statsData.allPayInOrderSum?.completeOrderCount || 0) + '/' + Number(this.statsData.allPayInOrderSum?.totalOrderCount || 0) + ')',
            subType: 'percent',
            tag: '总',
            tagClass: 'tag-green',
            type: 'amount'
          },
          {
            title: '今日代付总额',
            value: Number(this.statsData.todayPayOutOrderSum?.totalOrderAmount || 0),
            subTitle: '代付订单数',
            subValue: formatAmount(Number(this.statsData.todayPayOutOrderSum?.completeOrderCount || 0), '', 0),
            subType: 'count',
            tag: '日',
            tagClass: 'tag-orange',
            type: 'amount'
          },
          {
            title: '昨日代付总额',
            value: Number(this.statsData.yesterdayPayOutOrderSum?.totalOrderAmount || 0),
            subTitle: '代付订单数',
            subValue: formatAmount(Number(this.statsData.yesterdayPayOutOrderSum?.completeOrderCount || 0), '', 0),
            subType: 'count',
            tag: '日',
            tagClass: 'tag-orange',
            type: 'amount'
          },
          {
            title: '代付总额',
            value: Number(this.statsData.allPayOutOrderSum?.totalOrderAmount || 0),
            subTitle: '代付订单总数',
            subValue: formatAmount(Number(this.statsData.allPayOutOrderSum?.completeOrderCount || 0), '', 0),
            subType: 'count',
            tag: '总',
            tagClass: 'tag-green',
            type: 'amount'
          },
          {
            title: '账户余额',
            value: Number(this.statsData.accountSumRS?.balanceAmount || 0),
            subTitle: '冻结金额',
            subValue: formatAmount(Number(this.statsData.accountSumRS?.frozenAmount || 0), '', 2),
            tag: '总',
            tagClass: 'tag-green',
            type: 'amount'
          },
          {
            title: '可结算金额',
            value: Number(this.statsData.accountSumRS?.availableSettlementAmount || 0),
            subTitle: '未结算金额',
            subValue: formatAmount(Number(this.statsData.accountSumRS?.inggien || 0), '', 2),
            subType: 'amount',
            tag: '总',
            tagClass: 'tag-green',
            type: 'amount'
          }
          // {
          //   title: '下单总金额',
          //   value: this.statsData.allPayOutOrderSum?.totalOrderAmount || 0,
          //   subTitle: '下单冻结金额',
          //   subValue: this.statsData.accountSumRS?.frozenAmount || 0,
          //   subType: 'amount',
          //   tag: '总',
          //   tagClass: 'tag-green',
          //   type: 'amount'
          // }
        ]
        // console.log('Generated statsList:', statsList)
        resolve(statsList)
      })
    },

    // 手动刷新卡片（用于调试）
    manualRefresh () {
      // console.log('Manual refresh triggered')
      this.refreshCard()
    },

    // 格式化数值显示
    formatValue (value, type) {
      if (value === null || value === undefined || value === '') {
        return '--'
      }

      // 确保转换为数字类型
      const numValue = Number(value)
      if (isNaN(numValue)) {
        return '--'
      }

      if (type === 'percent') {
        return numValue.toFixed(2) + '%'
      } else if (type === 'amount') {
        return formatAmount(numValue, '', 2)
      } else if (type === 'count') {
        return formatAmount(numValue, '', 0)
      } else {
        return formatAmount(numValue, '', 0)
      }
    },

    // 刷新卡片
    refreshCard () {
      // 当数据更新时，通过改变 key 来强制重新渲染 JeepayCard
      this.cardKey += 1
      // console.log('Refreshing card with key:', this.cardKey)
    }
  }
}
</script>

<style lang="less" scoped>
.merchant-overview {
  margin-bottom: 12px;
  // min-height: 500px;

  .section-title {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 16px;
    color: #333;
  }

  .dashboard-stat-card {
    position: relative;
    padding: 16px;
    height: 100%;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border: 1px solid #f0f0f0;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }

    .card-tag {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      color: #fff;

      &.tag-orange {
        background: linear-gradient(135deg, #ff9500, #ff7700);
      }

      &.tag-green {
        background: linear-gradient(135deg, #52c41a, #389e0d);
      }
    }

    .main-data {
      margin-bottom: 12px;

      .main-value {
        font-size: 28px;
        font-weight: bold;
        color: #262626;
        line-height: 1.2;
        margin-bottom: 4px;
      }

      .main-title {
        font-size: 14px;
        color: #595959;
        font-weight: 500;
      }
    }

    .sub-data {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 8px;
      border-top: 1px solid #f5f5f5;

      .sub-label {
        font-size: 12px;
        color: #8c8c8c;
      }

      .sub-value {
        font-size: 14px;
        font-weight: 600;
        color: #1890ff;
      }
    }
  }

  .dashboard-skeleton-list {
    display: block;
    // margin-bottom: 16px;
  }
  .dashboard-skeleton-item {
    flex: none;
    min-width: 100%;
    max-width: 100%;

  }
  .dashboard-skeleton-item + .dashboard-skeleton-item {
    margin-top: 12px;
  }
  .dashboard-skeleton-item-col {
    // padding-top: 24px;
    // padding-bottom: 16px;
    margin-bottom: 0;
    display: block;
    justify-content: unset;
  }
  .dashboard-skeleton-item-col-content {
    padding: 12px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    border: 1px solid #f0f0f0;
    transition: all 0.3s ease;
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }
  }
}
</style>
